import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import {
  ConfirmationService,
  MessageService,
  PrimeNGConfig,
} from "primeng/api";
import { FileUsageService } from "../../../_services/file-usage.service";
import { Subscription } from "rxjs";
import { BaseLocationModel } from "@cas-hub/acabim-bim-visualiser-client";
import {
  AcabimProject,
  AcabimSite,
  Building,
  BuildingApiService,
  BuildingElementFunction,
  BuildingElementFunctionApiService,
  BuildingElementFunctionName,
  BuildingElementFunctionType,
  BuildingElementType,
  BuildingElementUpdateRequest,
  BuildingStorey,
  ColumnFilter,
  DataTableColumnData,
  DataTableConfig,
  DataTableDataSource,
  DataTableObservableCompletionListener,
  DataTableObservableDataSource,
  DataTableObservableErrorListener,
  DataTableObservableRefresher,
  DatatableComponent,
  MultiFileUploadComponent,
  MultifileService,
  OmniClass,
  OmniClassApiService,
  ProjectApiService,
  LocationService,
  SiteApiService,
  TableAction,
} from "@cas-hub/acabim-common-client";

@Component({
  selector: "acabim-admin-building-management",
  templateUrl: "./building-management.component.html",
  styleUrls: ["./building-management.component.scss"],
  providers: [MessageService],
})
export class BuildingManagementComponent implements OnInit, OnDestroy {
  @ViewChild(DatatableComponent) public dataTable?: DatatableComponent;
  @ViewChild("fileComp") public fileComponent?: MultiFileUploadComponent;

  public fileGuids: string[] = [];
  public fullFiles: {
    guid: string;
    name: string;
    loading: boolean;
    file: any;
  }[] = [];
  public dataSources: DataTableDataSource<any>[] = [];
  public mimetypes: string[] = ["application/pdf", "image/png", "image/jpeg"];

  addModal = false;
  currentBuilding?: Building;
  currentStoreyData: BuildingStorey[] = [];
  deleteModal = false;
  disableAdd = false;
  editForm: any;
  floorArea = 0;
  functions: OmniClass[] = [];
  hasFunction = false;
  height = 0;
  index = 0;
  isLoading = false;
  selectedFuncs: BuildingElementFunction[] = [];
  selectedProject?: any;
  selectedSite?: AcabimSite;

  projectSub: Subscription = new Subscription();
  project2Sub: Subscription = new Subscription();
  siteSub: Subscription = new Subscription();

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private primengConfig: PrimeNGConfig,
    private readonly buildingApiService: BuildingApiService,
    private readonly buildingElementFunctionService: BuildingElementFunctionApiService,
    private readonly fileService: FileUsageService,
    private readonly fileServiceCommon: MultifileService,
    private readonly omniClass: OmniClassApiService,
    private readonly projectApiService: ProjectApiService,
    private readonly locationService: LocationService,
    private readonly siteApiService: SiteApiService
  ) {}

  private get buildingDataRefresher(): DataTableObservableRefresher<BuildingElementFunction> {
    return () =>
      this.buildingElementFunctionService.getFunctions({
        elementType: BuildingElementType.building,
        elementGuid: this.selectedProject?.buildingGuid,
      });
  }

  ngOnInit(): void {
    this.initForm();
    this.primengConfig.ripple = true;
    this.getLocation();

    this.dataSources.push(
      new BuildingManagementDS(
        "",
        this.buildingDataRefresher,
        undefined,
        undefined,
        (action) => {
          if (action.name === "DELETE") {
            this.selectedFuncs = this.dataSources[0].selectedData;
            this.deleteModal = true;
          }
        }
      )
    );
    this.omniClass.search(undefined, 13).subscribe((data: OmniClass[]) => {
      this.functions = data;
      // tslint:disable-next-line:only-arrow-functions
      this.functions.sort(function (a, b) {
        const textA = a.name.toUpperCase();
        const textB = b.name.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      });
    });
  }

  ngOnDestroy(): void {
    this.siteSub.unsubscribe();
    this.projectSub.unsubscribe();
    this.project2Sub.unsubscribe();
  }

  selectSite(site: AcabimSite): void {
    this.selectedSite = site;
  }

  getLocation(): void {
    this.siteSub = this.siteApiService.selectedSite$.subscribe((site) => {
      if (site) {
        this.selectedSite = site;
        this.projectSub = this.projectApiService.selectedProject$.subscribe(
          (project) => {
            if (project) {
              this.selectedProject = project;
              this.selectBuilding(project);
            } else {
              this.selectedProject = undefined;
            }
          }
        );
      } else {
        this.selectedSite = undefined;
      }
    });
    this.project2Sub = this.locationService
      .getActiveProject()
      .subscribe((p) => {
        if (p && this.selectedProject.id !== p) {
          this.selectedProject = p;
        }
      });
  }

  initForm(): void {
    this.editForm = new FormGroup({
      elevation: new FormControl(""),
      function: new FormControl(""),
      buildingNumber: new FormControl(""),
      buildingName: new FormControl(""),
      buildingLongName: new FormControl(""),
      buildingDescription: new FormControl(""),
      constructionStartDate: new FormControl(""),
      currentPhase: new FormControl(""),
      remarks: new FormControl(""),
    });
  }

  onSubmit(): void {
    if (!this.currentBuilding || !this.selectedProject) {
      return;
    }

    this.isLoading = true;

    const updatedProperties: Record<string, any> = {};
    if (this.editForm.getRawValue().elevation) {
      updatedProperties.elevationOfTerrain =
        Number(this.editForm.getRawValue().elevation) * 1000;
    }

    if (this.editForm.getRawValue().buildingNumber) {
      updatedProperties.buildingNumber =
        this.editForm.getRawValue().buildingNumber;
    }

    if (this.editForm.getRawValue().buildingName) {
      updatedProperties.name = this.editForm.getRawValue().buildingName;
    }

    if (this.editForm.getRawValue().buildingLongName) {
      updatedProperties.longName = this.editForm.getRawValue().buildingLongName;
    }

    if (this.editForm.getRawValue().buildingDescription) {
      updatedProperties.description =
        this.editForm.getRawValue().buildingDescription;
    }

    if (this.editForm.getRawValue().remarks) {
      updatedProperties.remarks = this.editForm.getRawValue().remarks;
    }

    const request: BuildingElementUpdateRequest = {
      guid: this.currentBuilding.guid,
      projectId: this.selectedProject.id,
      elementType: BuildingElementType.building,
      updatedProperties,
    };

    this.buildingApiService.updateBuildingElement(request).subscribe(
      (_: any) => {
        this.isLoading = false;
        this.messageService.clear();
        this.messageService.add({
          severity: "success",
          summary: "Successful",
          detail: "Building Properties Updated",
        });
      },
      (error: any) => {
        this.isLoading = false;
        this.messageService.clear();
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: error
            ? error.error
              ? error.error.title
              : "Sorry, the building properties cannot be updated"
            : "Sorry, the building properties cannot be updated",
        });
      }
    );
  }

  selectLocation(location: BaseLocationModel): void {
    if (location.site) {
      this.selectSite(location.site);
    }
    if (location.project) {
      this.selectBuilding(location.project);
    }
  }

  selectBuilding(project: AcabimProject): void {
    this.selectedProject = project;
    this.editForm.reset();
    this.editForm.get("function").enable();
    if (this.selectedProject && this.selectedProject.buildingGuid) {
      this.isLoading = true;
      this.buildingApiService
        .getBuildingStories(this.selectedProject?.id)
        .subscribe((value) => {
          this.currentStoreyData = value;
          const elevation: number[] = [];
          // this.floorArea = 0;
          this.currentStoreyData.forEach((storey) => {
            elevation.push(storey.elevation);
            // this.floorArea = this.floorArea + storey.grossFloorArea;
          });
          const max = elevation.reduce((a, b) => Math.max(a, b));
          const min = elevation.reduce((a, b) => Math.min(a, b));
          this.height = (max - min) / 1000;
          // this.floorArea = this.floorArea / 1000000;
        });
      this.buildingApiService
        .getBuildingInfo(this.selectedProject.id)
        .subscribe((building) => {
          console.log(building);
          this.currentBuilding = building;
          if (this.currentBuilding) {
            this.editForm
              .get("elevation")
              .patchValue(
                this.currentBuilding.elevationOfTerrain
                  ? (this.currentBuilding.elevationOfTerrain / 1000)
                      .toFixed(2)
                      .toString()
                  : "0"
              );
            this.editForm
              .get("remarks")
              .patchValue(
                this.currentBuilding.remarks ? this.currentBuilding.remarks : ""
              );
            this.editForm
              .get("buildingNumber")
              .patchValue(
                this.currentBuilding.buildingNumber
                  ? this.currentBuilding.buildingNumber
                  : ""
              );
            this.editForm
              .get("buildingName")
              .patchValue(
                this.currentBuilding.name ? this.currentBuilding.name : ""
              );
            this.editForm
              .get("buildingLongName")
              .patchValue(
                this.currentBuilding.longName
                  ? this.currentBuilding.longName
                  : ""
              );
            this.editForm
              .get("buildingDescription")
              .patchValue(
                this.currentBuilding.description
                  ? this.currentBuilding.description
                  : ""
              );
            this.floorArea = building.gfa / 1000000;
            console.log(this.floorArea);
          }
          this.isLoading = false;
        });
      this.buildingElementFunctionService
        .getFunctions({
          elementGuid: this.selectedProject.buildingGuid,
          functionName: BuildingElementFunctionName.OMNICLASS,
          functionType: BuildingElementFunctionType.STANDARDS,
          elementType: BuildingElementType.building,
        })
        .subscribe((data) => {
          if (data.length > 0) {
            this.hasFunction = true;
            this.editForm.get("function").disable();

            this.functions.forEach((func) => {
              if (func.number === data[0].value) {
                this.editForm.get("function").patchValue(func);
              }
            });
          }
        });
      this.getFiles(this.selectedProject.buildingGuid);
      if (this.dataTable) {
        this.dataTable.requestImmediateDataRefresh();
      }
    }
  }

  addFunction() {
    if (this.currentBuilding) {
      this.buildingElementFunctionService
        .createFunction({
          remarks: "Building Usage",
          userDefinedName: "",
          value: this.editForm.getRawValue().function.number,
          buildingElementType: BuildingElementType.building,
          buildingElementGuid: this.currentBuilding.guid,
          predefinedType: BuildingElementFunctionType.STANDARDS,
          predefinedName: BuildingElementFunctionName.OMNICLASS,
        })
        .subscribe((data) => {
          this.editForm.get("function").disable();

          this.hasFunction = true;
          if (this.dataTable) {
            this.dataTable.requestImmediateDataRefresh();
          }
        });
    }
  }

  setAddStatus($event: boolean): void {
    this.addModal = $event;
  }

  setDeleteStatus($event: boolean): void {
    this.deleteModal = $event;
  }

  closeAdd() {
    this.addModal = false;
    // tslint:disable-next-line:no-non-null-assertion
    this.dataTable!.requestImmediateDataRefresh();
  }

  closeDeleteModal() {
    this.deleteModal = false;
    this.selectedFuncs = [];
    this.buildingElementFunctionService
      .getFunctions({
        // @ts-ignore
        elementGuid: this.selectedProject.buildingGuid,
        functionName: BuildingElementFunctionName.OMNICLASS,
        functionType: BuildingElementFunctionType.STANDARDS,
        elementType: BuildingElementType.building,
      })
      .subscribe((data) => {
        if (data.length > 0) {
          this.editForm.get("function").disable();

          this.hasFunction = true;
          this.functions.forEach((func) => {
            if (func.number === data[0].value) {
              this.editForm.get("function").patchValue(func);
            }
          });
        } else {
          this.editForm.get("function").patchValue("");
          this.editForm.get("function").enable();
          this.hasFunction = false;
        }
      });
    // tslint:disable-next-line:no-non-null-assertion
    this.dataTable!.requestImmediateDataRefresh();
  }

  addImages(): void {
    this.isLoading = true;
    // tslint:disable-next-line:no-non-null-assertion
    this.fileService
      .uploadFile(this.currentBuilding!.guid, this.fileGuids)
      .subscribe(
        (data) => {
          this.fileGuids = [];
          this.fullFiles = [];
          this.fileComponent?.resetImages();
          this.isLoading = false;
          // tslint:disable-next-line:no-non-null-assertion
          this.getFiles(this.currentBuilding!.guid);
          this.messageService.clear();
          this.messageService.add({
            key: "attachment",
            severity: "success",
            summary: "attachments added",
          });
        },
        (error) => {
          this.isLoading = false;
          this.messageService.clear();
          this.messageService.add({
            key: "attachment",
            severity: "error",
            summary: "Sorry, cannot proceed with current attachments",
          });
        }
      );
  }

  onFileAdded(guid: string): void {
    this.fileGuids.push(guid);
  }

  onFileDeleted(guid: string): void {
    this.deleteFile(guid);
  }

  deleteFile(guid: string): void {
    this.fileGuids.splice(this.fileGuids.indexOf(guid), 1);
    // tslint:disable-next-line:no-non-null-assertion
    this.fileService.removeFiles(this.currentBuilding!.guid, [guid]).subscribe(
      (data) => {
        this.messageService.clear();
        this.messageService.add({
          severity: "success",
          summary: "Attachment removed",
        });
      },
      (error) => {}
    );
  }

  addToBuilding(): void {
    if (this.fileGuids.length > 0) {
      this.confirmationService.confirm({
        key: "confirmAttachment",
        message: "Would you like to proceed?",
        header: "Confirmation",
        icon: "pi pi-exclamation-triangle",
        accept: () => {
          this.addImages();
        },
        reject: () => {},
      });
    }
  }

  onFileLoading($event: boolean): void {
    this.disableAdd = $event;
  }

  getFiles(buildGUID: string): void {
    this.fileService.getFilesFromObject(buildGUID).subscribe((files) => {
      files.forEach((f) => {
        const attachment = f.guid;
        const index = this.fullFiles.length;
        this.fullFiles.push({
          guid: attachment,
          name: f.name,
          loading: true,
          file: null,
        });
        this.fileServiceCommon.getFile(attachment).subscribe(
          (file) => {
            const reader = new FileReader();
            reader.addEventListener(
              "load",
              () => {
                this.fullFiles[index].file = reader.result;
              },
              false
            );
            if (file) {
              reader.readAsDataURL(file);
            }
            this.fullFiles[index].loading = false;
          },
          (error) => {
            this.fullFiles[index].loading = false;
          }
        );
      });
    });
  }

  selectTab($event: any): void {}
}

export class BuildingManagementDS<
  T extends BuildingElementFunction
> extends DataTableObservableDataSource<T> {
  readonly columnData: DataTableColumnData[] = [
    { displayName: "Type", objectPropertyName: "predefinedType" },
    { displayName: "Name", objectPropertyName: "predefinedName" },
    { displayName: "Value", objectPropertyName: "value" },
    { displayName: "Remarks", objectPropertyName: "remarks" },
  ];
  config: DataTableConfig;
  columnFilters: ColumnFilter[] = [];
  actions: TableAction[] = [{ name: "DELETE", id: 0 }];

  onDataExpanded(value: BuildingElementFunction): void {}

  constructor(
    readonly tableTitle: string,
    readonly dataRefresher: DataTableObservableRefresher<T>,
    readonly observableErrorListener?: DataTableObservableErrorListener,
    readonly observableCompleteListener?: DataTableObservableCompletionListener,
    readonly actionListener?: (action: TableAction) => void
  ) {
    super(() => this.dataRefresher(), undefined, undefined);

    this.config = {
      tableHeading: "",
      isSearchEnabled: false,
      searchPlaceHolder: "",
      emptyMessage: "No function found",
      globalFilterFields: [],
      hasCheckbox: true,
      hasDetailsButton: false,
      isLazyLoadingEnabled: false,
      isPaginationEnabled: false,
      hasFilters: false,
      isExportEnabled: false,
      hasActions: true,
    };
  }

  onAction(action: TableAction): void {
    if (this.actionListener) {
      this.actionListener(action);
    }
  }

  onDataFiltered(): void {}
}
